import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { EntityVisibilitySelect } from '~/components/Shared/EntityVisibilitySelect/EntityVisibilitySelect';
import type { EntityVisibilityType } from '~/components/Shared/EntityVisibilitySelect/types';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { SavedDashboardsActions } from '~/enums/SavedDashboards';
import { SaveSearchPermissionType } from '~/enums/SaveSearchPermissionType';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import * as ToastTypes from '~/types/toast.types';

export function ShareDashboard() {
  const intl = useIntl();
  const activeModal = useSelector(
    slices.savedDashboards.selectors.selectActiveModal,
  );
  const dispatch = useDispatch();
  const dashboard = activeModal?.selectedDashboard;
  const [entityPermission, setEntityPermission] =
    useState<EntityVisibilityType>({
      visibility: dashboard?.visibility ?? SaveSearchPermissionType.Private,
    });
  const { currentData: dashboardInfo } = api.endpoints.getDashboardV2.useQuery(
    dashboard?.id ?? '',
    {
      skip:
        !dashboard || dashboard.visibility !== SaveSearchPermissionType.Shared,
    },
  );

  const [shareDashboard] = api.endpoints.updateDashboard.useMutation();

  const isMissingUserDepartments =
    entityPermission.visibility === SaveSearchPermissionType.Shared &&
    (!entityPermission.userIds || entityPermission.userIds?.length === 0) &&
    (!entityPermission.departmentIds ||
      entityPermission.departmentIds?.length === 0);
  const isShareDisabled = !dashboard || isMissingUserDepartments;

  useEffect(() => {
    if (dashboard) {
      setEntityPermission({
        visibility: dashboard.visibility as SaveSearchPermissionType,
      });
    }
  }, [dashboard]);

  useEffect(() => {
    if (dashboardInfo) {
      setEntityPermission({
        visibility: dashboardInfo.visibility as SaveSearchPermissionType,
        userIds: dashboardInfo.userIds,
        departmentIds: dashboardInfo.departmentIds,
      });
    }
  }, [dashboardInfo]);

  const onShare = async () => {
    const { visibility, userIds, departmentIds } = entityPermission;
    if (dashboard) {
      const payload = {
        uuid: dashboard?.id,
        attributes: {
          visibility,
          user_ids: userIds,
          department_ids: departmentIds,
        },
      };
      try {
        await shareDashboard(payload).unwrap();
        showToast(
          ToastTypes.SUCCESS,
          intl.formatMessage(
            {
              id: 'ShareDashboard.shareSuccess',
              defaultMessage: '{dashboardName} shared.',
              description:
                'Success message when dashboard is shared. Example: dashboardName = "Document Name"',
            },
            { dashboardName: dashboard.name },
          ),
        );
        dispatch(slices.savedDashboards.actions.closeActiveModal());
      } catch (error: any) {
        if (error.response.status === 403) {
          showToast(
            ToastTypes.ERROR,
            intl.formatMessage({
              id: 'ShareDashboard.noPermissionError',
              defaultMessage:
                'You do not have permission to share this dashboard.',
              description: 'Error message when user lacks permission to share',
            }),
          );
        } else {
          showToast(
            ToastTypes.ERROR,
            intl.formatMessage({
              id: 'ShareDashboard.shareError',
              defaultMessage: 'An error occurred while sharing the dashboard.',
              description: 'Generic error message when sharing fails',
            }),
          );
        }
      }
    }
  };

  return (
    <Modal
      overflow="visible"
      title={intl.formatMessage({
        id: 'ShareDashboard.modalTitle',
        defaultMessage: 'Share Dashboard',
        description: 'Title of the share dashboard modal',
      })}
      isVisible={activeModal?.name === SavedDashboardsActions.Share}
      onHide={() => {
        dispatch(slices.savedDashboards.actions.closeActiveModal());
      }}
      onCancel={() => {
        dispatch(slices.savedDashboards.actions.closeActiveModal());
      }}
      primaryAction={{
        disabled: isShareDisabled,
        text: intl.formatMessage({
          id: 'ShareDashboard.shareButton',
          defaultMessage: 'Share',
          description: 'Share button text',
        }),
        tooltip: !isShareDisabled
          ? intl.formatMessage({
              id: 'ShareDashboard.shareTooltip',
              defaultMessage: 'Share',
              description: 'Share button tooltip',
            })
          : intl.formatMessage({
              id: 'ShareDashboard.shareDisabledTooltip',
              defaultMessage:
                'Specify at least one user or department to share the dashboard.',
              description: 'Tooltip when share button is disabled',
            }),
        onClick: onShare,
      }}
    >
      <Layout direction="column" spacing={4}>
        <Layout direction="column">
          <Text variant="body-bold">
            {intl.formatMessage({
              id: 'ShareDashboard.dashboardNameLabel',
              defaultMessage: 'Dashboard name',
              description: 'Label for dashboard name field',
            })}
          </Text>
          <Text>{dashboard?.name}</Text>
        </Layout>
        <EntityVisibilitySelect
          value={entityPermission}
          label={intl.formatMessage({
            id: 'ShareDashboard.visibilityLabel',
            defaultMessage: 'Who can see and use this dashboard ?',
            description: 'Label for dashboard visibility selector',
          })}
          onChange={setEntityPermission}
        />
      </Layout>
    </Modal>
  );
}
